import React, { useMemo, useState } from 'react';
import axiosInstance from './../axiosInstance.js';
import { Button, NumberInput, Text } from '@mantine/core';

// Ordered list of platforms the admin can set ratings on. Names match the
// icon keys used by PlatformRatings and the ratingPlatformFromName map on
// the backend.
const PLATFORMS = [
  { name: 'imdb', label: 'IMDb', icon: '/imdb_small_icon.png' },
  { name: 'kinopoisk', label: 'Kinopoisk', icon: '/kinopoisk_small_icon.png' },
  { name: 'rottenTomatoes', label: 'Rotten Tomatoes (critics)', icon: '/rottentomatoes_icon.png' },
  { name: 'rottenTomatoesAudience', label: 'Rotten Tomatoes (audience)', icon: '/rottentomatoes_icon.png' },
  { name: 'amazon', label: 'Amazon', icon: '/amazon_icon.png' },
  { name: 'goodreads', label: 'Goodreads', icon: '/goodreads_icon.png' },
  { name: 'reddit', label: 'Reddit', icon: '/reddit_icon.png' },
  { name: 'appleBooks', label: 'Apple Books', icon: '/applebooks_icon.png' },
  { name: 'steam', label: 'Steam', icon: '/steam_icon.png' },
  { name: 'metacritic', label: 'Metacritic (critics)', icon: '/metacritic_icon.png' },
  { name: 'metacriticUser', label: 'Metacritic (users)', icon: '/metacritic_icon.png' },
  { name: 'livelib', label: 'Livelib', icon: '/livelib_icon.png' },
  { name: 'litres', label: 'Litres', icon: '/litres_icon.png' },
];

// AdminRatingsEditor renders a small table of every platform, with the
// current rating/votes pre-filled and inline inputs to overwrite or
// delete. Admin-only — parent gates on isAdmin before rendering.
// Collapsed by default: the whole panel is behind a toggle so it doesn't
// clutter the art page during normal browsing.
const AdminRatingsEditor = ({ art, onArtUpdated }) => {
  const [open, setOpen] = useState(false);

  const infosByName = useMemo(() => {
    const m = {};
    (art.otherPlatformInfos || []).forEach((info) => {
      m[info.name] = info;
    });
    return m;
  }, [art.otherPlatformInfos]);

  // drafts[name] = { rating, votes } — we always render controlled inputs
  // that fall back to the server value; local drafts win once the admin
  // types something.
  const [drafts, setDrafts] = useState({});
  const [savingPlatform, setSavingPlatform] = useState(null); // "amazon" while in flight

  const updateDraft = (name, patch) => {
    setDrafts((prev) => ({ ...prev, [name]: { ...(prev[name] || {}), ...patch } }));
  };

  const currentValues = (name) => {
    const info = infosByName[name];
    const draft = drafts[name] || {};
    return {
      rating: draft.rating !== undefined ? draft.rating : (info?.score ?? ''),
      votes: draft.votes !== undefined ? draft.votes : (info?.votes ?? ''),
    };
  };

  const handleSave = (name) => {
    const { rating, votes } = currentValues(name);
    const ratingNum = Number(rating);
    const votesNum = Number(votes || 0);
    if (!Number.isFinite(ratingNum) || ratingNum < 0) {
      alert('Rating must be a non-negative number.');
      return;
    }
    setSavingPlatform(name);
    axiosInstance
      .post('/admin/rating', { artId: art.id, platform: name, rating: ratingNum, votesCount: votesNum })
      .then(() => {
        setDrafts((prev) => ({ ...prev, [name]: undefined }));
        onArtUpdated && onArtUpdated();
      })
      .catch((err) => {
        console.error('Save rating failed', err);
        alert('Save failed: ' + (err.response?.data || err.message));
      })
      .finally(() => setSavingPlatform(null));
  };

  const handleDelete = (name) => {
    if (!window.confirm(`Delete ${name} rating?`)) return;
    setSavingPlatform(name);
    axiosInstance
      .post('/admin/rating', { artId: art.id, platform: name, delete: true })
      .then(() => {
        setDrafts((prev) => ({ ...prev, [name]: undefined }));
        onArtUpdated && onArtUpdated();
      })
      .catch((err) => {
        console.error('Delete rating failed', err);
        alert('Delete failed: ' + (err.response?.data || err.message));
      })
      .finally(() => setSavingPlatform(null));
  };

  if (!open) {
    return (
      <div className="mt-2 flex justify-center">
        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setOpen(true)}>
          Edit ratings
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 border rounded-md p-3" style={{ borderColor: 'var(--mantine-color-default-border)' }}>
      <div className="flex items-center justify-between mb-2">
        <Text size="xs" c="dimmed">Admin: platform ratings</Text>
        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setOpen(false)}>
          Hide
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        {PLATFORMS.map(({ name, label, icon }) => {
          const { rating, votes } = currentValues(name);
          const existing = infosByName[name];
          const busy = savingPlatform === name;
          return (
            <div key={name} className="flex items-center gap-2 flex-wrap">
              <img src={icon} alt={label} className="w-4 h-4 rounded-sm" />
              <span className="text-xs w-40 truncate" title={label}>{label}</span>
              <NumberInput
                size="xs"
                w={90}
                placeholder="rating"
                value={rating}
                min={0}
                step={0.1}
                decimalScale={2}
                hideControls
                onChange={(v) => updateDraft(name, { rating: v })}
              />
              <NumberInput
                size="xs"
                w={100}
                placeholder="votes"
                value={votes}
                min={0}
                hideControls
                onChange={(v) => updateDraft(name, { votes: v })}
              />
              <Button size="compact-xs" variant="light" loading={busy} onClick={() => handleSave(name)}>
                Save
              </Button>
              {existing && existing.score !== undefined && (
                <Button size="compact-xs" variant="subtle" color="red" loading={busy} onClick={() => handleDelete(name)}>
                  Delete
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminRatingsEditor;
